/**
 * Submission Dialog Component
 *
 * Main dialog for the PSC submission flow.
 */

import React, { useCallback, useEffect } from 'react';
import { usePSCContext } from '../hooks/usePSCContext';
import { TypeSelector } from './TypeSelector';
import { QuestionnaireForm } from './QuestionnaireForm';
import { ContextPreview } from './ContextPreview';
import { SuccessScreen } from './SuccessScreen';
import { ScreenshotCapture } from './ScreenshotCapture';
import type { RequirementType } from '../types';
import clsx from 'clsx';

// Close icon
const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// Arrow left icon
const ArrowLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);

const STEPS = ['Type', 'Details', 'Review', 'Done'];

interface FormData {
  title: string;
  description: string;
  priority: string;
  stepsToReproduce?: string;
  expectedBehavior?: string;
  actualBehavior?: string;
  category?: string;
}

export function SubmissionDialog() {
  const {
    config,
    isOpen,
    close,
    context,
    requirementType,
    setRequirementType,
    currentStep,
    setCurrentStep,
    submitRequirement,
    isSubmitting,
    error,
    clearError,
    isRecording,
    draftFormData,
    setDraftFormData,
    draftScreenshots,
    setDraftScreenshots,
    submittedId,
    setSubmittedId,
    resetDraft,
  } = usePSCContext();

  const [includeRecording, setIncludeRecording] = React.useState(true);

  // Stable callback for screenshots to avoid infinite re-render loop
  const handleScreenshotsChange = useCallback((shots: any[]) => {
    setDraftScreenshots(shots);
  }, [setDraftScreenshots]);

  // Handle type selection
  const handleTypeSelect = useCallback(
    (type: RequirementType) => {
      setRequirementType(type);
      setCurrentStep(1);
    },
    [setRequirementType, setCurrentStep]
  );

  // Handle form submission (to review step)
  const handleFormSubmit = useCallback((data: FormData) => {
    setDraftFormData(data);
    setCurrentStep(2);
  }, [setDraftFormData, setCurrentStep]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep, setCurrentStep]);

  // Handle final submission
  const handleFinalSubmit = useCallback(async () => {
    if (!requirementType || !draftFormData) return;

    try {
      const id = await submitRequirement({
        type: requirementType,
        title: draftFormData.title,
        description: draftFormData.description,
        priority: draftFormData.priority,
        additionalData: {
          stepsToReproduce: draftFormData.stepsToReproduce,
          expectedBehavior: draftFormData.expectedBehavior,
          actualBehavior: draftFormData.actualBehavior,
          category: draftFormData.category,
        },
        screenshots: draftScreenshots.map((s) => ({
          dataUrl: s.dataUrl,
          captureMode: s.captureMode,
          width: s.width,
          height: s.height,
        })),
        includeRecording: includeRecording && isRecording,
      });
      setSubmittedId(id);
      setCurrentStep(3);
    } catch {
      // Error is handled by context
    }
  }, [requirementType, draftFormData, submitRequirement, draftScreenshots, includeRecording, isRecording, setSubmittedId, setCurrentStep]);

  // Handle close — preserves draft state for mid-form closes
  const handleClose = useCallback(() => {
    close();
  }, [close]);

  // Handle close from success screen — resets all draft state
  const handleSuccessClose = useCallback(() => {
    resetDraft();
    close();
  }, [resetDraft, close]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  return (
    <div
      className="psc-dialog-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
      style={{ zIndex: config.zIndex + 1 }}
      data-testid="psc-dialog"
    >
      <div className={clsx('psc-dialog', config.dialogClassName)} role="dialog" aria-modal="true">
        {/* Header */}
        <div className="psc-dialog__header">
          <div className="psc-dialog__header-left">
            {currentStep > 0 && currentStep < 3 && (
              <button
                onClick={handleBack}
                className="psc-dialog__back-button"
                aria-label="Go back"
              >
                <ArrowLeftIcon />
              </button>
            )}
            <h2 className="psc-dialog__title">
              {currentStep === 3 ? 'Submitted' : 'Submit Feedback'}
            </h2>
            <span className="psc-dialog__app-badge">{config.appName}</span>
          </div>
          <button
            onClick={handleClose}
            className="psc-dialog__close-button"
            aria-label="Close dialog"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Progress Indicator */}
        {currentStep < 3 && (
          <div className="psc-progress">
            {STEPS.slice(0, -1).map((label, index) => (
              <React.Fragment key={label}>
                <div
                  className={clsx(
                    'psc-progress__step',
                    index <= currentStep && 'psc-progress__step--active',
                    index < currentStep && 'psc-progress__step--completed'
                  )}
                >
                  <span className="psc-progress__step-number">{index + 1}</span>
                  <span className="psc-progress__step-label">{label}</span>
                </div>
                {index < STEPS.length - 2 && (
                  <div
                    className={clsx(
                      'psc-progress__line',
                      index < currentStep && 'psc-progress__line--completed'
                    )}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="psc-error-banner" role="alert">
            <span>{error}</span>
            <button onClick={clearError} aria-label="Dismiss error">
              <CloseIcon />
            </button>
          </div>
        )}

        {/* Content */}
        <div className="psc-dialog__content">
          {/* Step 0: Type Selection */}
          {currentStep === 0 && (
            <TypeSelector selectedType={requirementType} onSelect={handleTypeSelect} />
          )}

          {/* Step 1: Questionnaire Form */}
          {currentStep === 1 && requirementType && (
            <QuestionnaireForm
              type={requirementType}
              categories={config.categories}
              onSubmit={handleFormSubmit}
              onBack={handleBack}
              isSubmitting={isSubmitting}
              initialData={draftFormData}
              onFormDataChange={setDraftFormData}
            />
          )}

          {/* Step 2: Review & Submit */}
          {currentStep === 2 && draftFormData && (
            <div className="psc-review">
              <h3 className="psc-review__title">Review Your Submission</h3>

              {/* Summary */}
              <div className="psc-review__section">
                <h4 className="psc-review__section-title">Summary</h4>
                <dl className="psc-review__dl">
                  <dt>Type</dt>
                  <dd className="psc-review__type-badge">{requirementType?.replace('_', ' ')}</dd>
                  <dt>Title</dt>
                  <dd>{draftFormData.title}</dd>
                  <dt>Priority</dt>
                  <dd className={`psc-review__priority psc-review__priority--${draftFormData.priority}`}>
                    {draftFormData.priority}
                  </dd>
                  <dt>Description</dt>
                  <dd className="psc-review__description">{draftFormData.description}</dd>
                  {draftFormData.category && (
                    <>
                      <dt>Category</dt>
                      <dd>{draftFormData.category}</dd>
                    </>
                  )}
                </dl>
              </div>

              {/* Context Preview */}
              <div className="psc-review__section">
                <ContextPreview
                  context={context}
                  showConsoleErrors={config.captureConsoleErrors}
                  showNetworkErrors={config.captureNetworkErrors}
                  showEntities={config.captureEntities}
                />
              </div>

              {/* Screenshot Capture */}
              <div className="psc-review__section">
                <h4 className="psc-review__section-title">Screenshots (Optional)</h4>
                <ScreenshotCapture
                  onScreenshotsChange={handleScreenshotsChange}
                  maxScreenshots={5}
                />
              </div>

              {/* Session Recording */}
              {isRecording && (
                <div className="psc-review__section">
                  <h4 className="psc-review__section-title">Session Recording (Optional)</h4>
                  <label className="psc-checkbox">
                    <input
                      type="checkbox"
                      checked={includeRecording}
                      onChange={(e) => setIncludeRecording(e.target.checked)}
                    />
                    <span className="psc-checkbox__label">
                      Include last 30 seconds of session recording
                    </span>
                  </label>
                  <p className="psc-review__help-text">
                    Session recording captures your interactions to help reproduce the issue.
                    Sensitive data is automatically masked.
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="psc-form-actions">
                <button
                  onClick={handleBack}
                  className="psc-button psc-button--secondary"
                  disabled={isSubmitting}
                >
                  Back
                </button>
                <button
                  onClick={handleFinalSubmit}
                  className="psc-button psc-button--primary psc-button--success"
                  disabled={isSubmitting}
                  data-testid="psc-final-submit"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Success */}
          {currentStep === 3 && submittedId && (
            <SuccessScreen requirementId={submittedId} onClose={handleSuccessClose} />
          )}
        </div>
      </div>
    </div>
  );
}
